import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'
import { PHOTO } from '@/constants/media'

const FACTS = [
    { label: 'Lagring', text: 'Bildene lagres trygt i mange år, ikke på mobilen til en ansatt som slutter.' },
    { label: 'Markering', text: 'Tegn, pil og skriv rett på bildet, så alle ser hva det gjelder.' },
    { label: 'Sporbarhet', text: 'Hvert bilde er knyttet til prosjekt, sjekkliste og punkt, med tidspunkt.' },
    { label: 'Bevis', text: 'Klager kunden, har du dokumentasjonen klar på sekunder.' }
]

/** A photo gets marked up as you scroll: the circle and the note are drawn by the reader. */
export const PhotoProof: FC = (): ReactNode => {
    const figure = useRef<HTMLElement>(null)

    useScrollProgress(figure, { startAt: 0.85, endAt: 0.35 })

    return (
        <section id='bildedokumentasjon' className='tone-deep grain relative overflow-hidden py-24 md:py-36' data-header='dark'>
            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
                <div className='col-span-12 lg:col-span-5'>
                    <SectionLabel index='04'>Bildedokumentasjon</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text='Et bilde sier mer enn tusen ord.' />
                    </h2>
                    <p className='mt-6 max-w-md text-tone-muted t-body pretty'>
                        Ta bildet der feilen eller løsningen er. Svenn knytter det til riktig prosjekt og riktig punkt i sjekklisten, så du har beviset den dagen du trenger det.
                    </p>

                    <dl className='mt-12 border-t border-tone-line'>
                        {FACTS.map((fact, i) => (
                            <div
                                key={fact.label}
                                className='grid grid-cols-12 gap-4 border-b border-tone-line py-4'
                                data-reveal='rise'
                                style={{ '--d': i } as CSSProperties}
                            >
                                <dt className='t-label col-span-4 pt-1 text-signal'>{fact.label}</dt>
                                <dd className='col-span-8 text-tone-muted'>{fact.text}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <figure ref={figure} className='ks-proof col-span-12 lg:col-span-6 lg:col-start-7'>
                    <div className='relative aspect-[4/3] overflow-hidden bg-night-raised' data-reveal='clip'>
                        <img
                            src={PHOTO.findingCrackedTile}
                            alt='Gulvflis med en lang sprekk, fotografert som avvik'
                            loading='lazy'
                            decoding='async'
                            className='size-full object-cover'
                        />

                        <svg viewBox='0 0 900 675' className='absolute inset-0 size-full' preserveAspectRatio='none' aria-hidden='true'>
                            <ellipse cx='478' cy='318' rx='330' ry='92' pathLength={1} className='ks-mark' fill='none' stroke='#F96529' strokeWidth='6' strokeLinecap='round' transform='rotate(16 478 318)' />
                            <path d='M590 560 C 548 520, 556 470, 604 428' pathLength={1} className='ks-mark ks-mark-late' fill='none' stroke='#F96529' strokeWidth='6' strokeLinecap='round' />
                            <path d='M580 432 L606 426 L604 452' pathLength={1} className='ks-mark ks-mark-late' fill='none' stroke='#F96529' strokeWidth='6' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>

                        <p className='ks-note absolute bottom-[5%] right-[4%] max-w-[13rem] bg-signal px-3 py-2 text-sm font-semibold leading-snug text-night'>
                            Sprekk i flis. Må byttes.
                        </p>
                    </div>

                    <figcaption className='t-label mt-4 flex flex-wrap justify-between gap-x-6 gap-y-2 text-tone-muted'>
                        <span>Punkt 1.6 · Materialer uten feil? · Avvik</span>
                        <span className='tabular'>Lagret med prosjekt og sjekkliste</span>
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}
